'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './Table.module.css';

export interface TableColumn<T> {
  id: string;
  header: React.ReactNode;
  cell: (item: T, index: number) => React.ReactNode;
  width?: string;
  sortable?: boolean;
  sticky?: boolean;
}

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  className?: string;
  initialSort?: SortConfig | null;
  onSort?: (sortConfig: SortConfig) => void;
  onRowClick?: (item: T) => void;
}

function Table<T>({
  columns,
  data,
  className = '',
  initialSort,
  onSort,
  onRowClick,
}: TableProps<T>) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(initialSort || null);
  
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const stickyScrollContentRef = useRef<HTMLDivElement>(null);
  const isSyncingRef = useRef(false);

  // Scroll sync: sticky -> table
  const handleStickyScroll = useCallback(() => {
    if (isSyncingRef.current || !tableScrollRef.current || !stickyScrollRef.current) return;
    
    isSyncingRef.current = true;
    requestAnimationFrame(() => {
      if (tableScrollRef.current && stickyScrollRef.current) {
        tableScrollRef.current.scrollLeft = stickyScrollRef.current.scrollLeft;
      }
      isSyncingRef.current = false;
    });
  }, []);

  // ResizeObserver for sticky scrollbar content width
  useEffect(() => {
    if (!tableScrollRef.current || !stickyScrollContentRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (tableScrollRef.current && stickyScrollContentRef.current) {
        stickyScrollContentRef.current.style.width = `${tableScrollRef.current.scrollWidth}px`;
      }
    });

    resizeObserver.observe(tableScrollRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const handleHeaderClick = useCallback((columnId: string, sortable?: boolean) => {
    if (!sortable) return;

    let direction: SortDirection = 'asc';

    if (sortConfig && sortConfig.key === columnId) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }

    const newSortConfig = direction ? { key: columnId, direction } : null;
    setSortConfig(newSortConfig);

    if (onSort && newSortConfig) {
      onSort(newSortConfig);
    }
  }, [sortConfig, onSort]);

  const getSortIcon = useCallback((columnId: string, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortConfig && sortConfig.key === columnId) {
      if (sortConfig.direction === 'asc') {
        return <span className={styles.sortIcon}>▲</span>;
      } else if (sortConfig.direction === 'desc') {
        return <span className={styles.sortIcon}>▼</span>;
      }
    }
    return <span className={styles.sortIconInactive}>◆</span>;
  }, [sortConfig]);

  return (
      <div className={styles.tableFrame}>
          <div 
              ref={tableScrollRef}
              className={styles.tableScroll}
      >
              <table className={styles.table}>
                  <thead>
                      <tr>
                          {columns.map((column) => (
                              <th
                                  key={column.id}
                                  className={`${styles.headerCell} ${column.sortable ? styles.sortableHeader : ''} ${column.sticky ? styles.sticky : ''}`}
                                  style={column.width ? { width: column.width } : undefined}
                                  onClick={() => handleHeaderClick(column.id, column.sortable)}
                >
                                  <div className={styles.headerContent}>
                                      {column.header}
                                      {getSortIcon(column.id, column.sortable)}
                                  </div>
                              </th>
              ))}
                      </tr>
                  </thead>
                  <tbody>
                      {data?.map((item, index) => (
                          <tr 
                              key={index} 
                              className={styles.tableRow} 
                              onClick={() => onRowClick?.(item)}
                              >
                              {columns.map((column) => (
                                  <td
                                      key={column.id}
                                      className={`${styles.tableCell} ${column?.sticky ? styles.sticky : ''}`}
                                      style={column.width ? { width: column.width } : undefined}
                                      >
                                      {column.cell(item, index)}
                                  </td>
                                ))}
                          </tr>
                        ))}
                  </tbody>
              </table>
          </div>
          <div 
              ref={stickyScrollRef}
              className={styles.stickyScrollbar}
              onScroll={handleStickyScroll}
      >
              <div ref={stickyScrollContentRef} className={styles.stickyScrollbarContent} />
          </div>
      </div>
  );
}

export default Table;
